<template>
  <div
    v-if="(!isEditMode && activeCanvasSections.length) || isEditMode"
    ref="sectionListRef"
    class="canvas-section-list-container rounded-2 w-full"
    :class="{
      'flex flex-col h-full justify-around gap-2 items-center': isDefaultDisplayMode,
      'bg-white': isEditMode,
    }"
    :style="isEditMode ? {} : sectionListContainerStyle"
  >
    <ContentListItems
      v-if="isEditMode"
      :hasSectionSelectorApp="hasSectionSelectorApp"
      :items="activeCanvasSections"
      :name="sectionListName"
      @open-section="(section, index) => (sectionItemInModal = { section, index })"
      @select-sections="onSectionSelectorToggle"
    />

    <div v-else-if="!activeCanvasSections.length" class="canvas-section-list__idle-state w-full">
      <div v-if="sectionListName" class="tracking-wide font-bold uppercase text-truncate mt-4 ml-4">
        {{ sectionListName }}
      </div>
      <div class="flex flex-col gap-4">
        <div v-if="suggestedSections.length" class="canvas-section-list__idle-state__title m-auto">
          {{ t('canvasUI.canvasBuilder.contentList.chooseFromRecommendations') }}
        </div>
        <div v-if="!hasSectionSelectorApp || isAdmin" class="w-full flex justify-end">
          <CButton
            class="my-2 mr-2"
            :disabled="isSavingCanvas"
            :style="{ fontFamily: 'var(--font-family)' }"
            @mousedown.stop="onSectionSelectorToggle"
          >
            {{ t('canvasUI.canvasBuilder.contentList.browseAll') }}
          </CButton>
          <CButton
            v-if="suggestedSections.length"
            class="my-2 mr-2"
            :disabled="!suggestedSections.some((s) => s.selected) || isSavingCanvas"
            type="primary"
            @click="keepRecommendedSections"
          >
            <span>{{ t('canvasUI.canvasBuilder.contentList.keep') }}</span>
          </CButton>
        </div>
      </div>
    </div>

    <!-- only show page by page sections in presentation mode -->
    <template v-else-if="!isEditMode">
      <div
        v-for="{ section, index: sectionIdx } in displayedSections"
        :key="section.id + sectionIdx"
        v-element-visibility="
          (isVisible: boolean) =>
            onSectionVisibility(isVisible, section, isTocEnhancementsEnabled ? sectionIdx : undefined)
        "
        class="canvas-section flex flex-col rounded-2 w-full overflow-auto"
        :class="{
          'gap-8 justify-center h-full': isDefaultDisplayMode,
          [getAnimationClass()]: !!data.animation_in && !!animationDirection,
        }"
        :data-section-id="section.id"
        :data-section-index="sectionIdx"
        :style="{ ...style, ...getSectionThemeStyle(section, style) }"
      >
        <video
          v-if="section.theme?.background_video_url"
          :key="section.theme?.background_video_url"
          autoplay
          class="absolute top-0 left-0 z-1 w-full h-full object-cover"
          loop
          muted
          :src="section.theme?.background_video_url"
        />

        <SectionInfoProvider
          :print-mode-idx="printModeIdx"
          :section-idx="sectionIdx"
          :section-list-id="id"
          :used-in-section-id="section.id"
        >
          <div
            class="canvas-section__content flex flex-col relative"
            :class="{
              'rounded-2 pa-4': !launchDarkly.enable_custom_display_overrides,
              'z-2': launchDarkly.disable_z_index_experiments || !!section.theme?.background_video_url,
            }"
            vertical
          >
            <div v-if="sectionListName" class="tracking-wide font-bold uppercase text-truncate">
              {{ sectionListName }}
            </div>

            <template v-for="(c, cIdx) in sectionContentById[section.id].content" :key="c.id">
              <div
                v-if="shouldRenderComponentWrapper(c, section.id)"
                v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, c)"
                class="component-wrapper relative"
                :data-section-component-id="c.id"
                :data-section-index="sectionIdx"
                :style="getComponentStyle(c, section, cIdx, sectionContentById[section.id].content)"
              >
                <LinkableComponent :id="c.id" :used-in-section-id="section.id">
                  <Component
                    :is="componentMap[c.type]"
                    v-bind="getComponentBindings(c)"
                    :used-in-section-id="section.id"
                  />
                </LinkableComponent>
              </div>
            </template>
          </div>
        </SectionInfoProvider>
      </div>
    </template>

    <template v-for="app in filteredSectionHookApps" :key="app.name">
      <iframe
        v-if="app.content_url"
        :ref="(el) => setCanvasHookRefs(el as HTMLIFrameElement, app)"
        height="0"
        :src="getEntryForSectionHook(app)"
        style="border: none; display: block"
        :title="app.name"
      />
    </template>
    <template v-if="showSelector">
      <SectionSelectorWithDragAndDrop
        v-if="launchDarkly.enable_selectors_dnd"
        :is-saving-canvas="isSavingCanvas"
        :selected-section-ids="selectedSectionIds"
        :selection-strategy="data.selection_strategy"
        @cancel="showSelector = false"
        @select="onSectionsSelect"
      />
      <SectionSelector
        v-else
        :is-saving-canvas="isSavingCanvas"
        :selected-section-ids="selectedSectionIds"
        :selection-strategy="data.selection_strategy"
        @cancel="showSelector = false"
        @select="onSectionsSelect"
      />
    </template>
  </div>

  <SectionItemModal
    v-if="sectionItemInModal"
    attach-to=".canvas-builder-app"
    :index="sectionItemInModal.index"
    :save-canvas="() => saveCanvasContentWithContext(activeCanvas?.context || {})"
    :section="sectionItemInModal.section"
    @close="sectionItemInModal = null"
  >
    <template #default="{ section, index }">
      <PageWrapperPaginated
        :aspect-ratio="aspectRatio"
        :aspect-ratio-style="aspectRatioStyle"
        :is-scale-content-enabled="isScaleEnabled"
      >
        <SectionListItem
          :id="id"
          class="flex-auto justify-start"
          :component-map="componentMap"
          :get-component-bindings="getComponentBindings"
          :get-component-style="getComponentStyle"
          :get-section-theme-style="getSectionThemeStyle"
          :index="index"
          :is-section-loaded="isSectionLoaded"
          :on-element-visibility="onElementVisibility"
          :on-section-visibility="onSectionVisibility"
          :section="section"
          :section-content-by-id="sectionContentById"
          :style="style"
        />
      </PageWrapperPaginated>
    </template>
  </SectionItemModal>
</template>

<script setup lang="ts">
import type { CSSProperties, ComputedRef } from 'vue'
import { ref, computed, onMounted, inject, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { isEqual, omit, cloneDeep } from 'lodash-es'
import { vElementVisibility } from '@vueuse/components'

import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import SectionSelector from '@canvas-builder/components/ui/SectionList/SectionSelector.vue'
import SectionSelectorWithDragAndDrop from '@canvas-builder/components/ui/SectionList/SectionSelectorWithDragAndDrop.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasNodes from '@canvas-builder/composables/useCanvasNodes'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import useThemeVars from '@lib/composables/useThemeVars'
import Grid from '@canvas-builder/components/ui/Grid/Grid.vue'
import Carousel from '@canvas-builder/components/ui/Carousel/Carousel.vue'
import ContentGrid from '@canvas-builder/components/ui/ContentGrid/ContentGrid.vue'
import DataCharts from '@canvas-builder/components/ui/DataCharts/DataCharts.vue'
import ContentListItems from './ContentListItems.vue'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import IFrame from '@canvas-builder/components/ui/IFrame/IFrame.vue'
import CollectionPlayer from '@canvas-builder/components/ui/CollectionPlayer/CollectionPlayer.vue'
import Scribble from '@canvas-builder/components/ui/Scribble/Scribble.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import Link from '@canvas-builder/components/ui/Link/Link.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import Timeline from '@canvas-builder/components/ui/Timeline/Timeline.vue'
import type { CanvasRetrieve, CanvasSection, FileRetrieve } from '@openapi'
import {
  ComponentTypes,
  CanvasBuilderMode,
  type ComponentNode,
  type CanvasBuilderProps,
  type ComponentTrackingParams,
  type ComponentNodeMultimediaThemeMeta,
  type ContentListProps,
  type ContentListSection,
} from '@canvas-builder/types/canvas'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useCanvasHooks from '@canvas-builder/composables/useCanvasHooks'
import useEmbeddable from '@canvas-builder/composables/useEmbeddable'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { type AppFile } from '@lib/types/app'
import { deepToRaw } from '@lib/util/reactive'
import useToast from '@lib/composables/useToast'
import { CANVAS_HOOKS } from '@lib/types/iframes'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import { getSectionGlobalComponentSpacing } from '@canvas-builder/util/canvas.util'
import usePrintMode from '@canvas-builder/composables/usePrintMode'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'
import SectionInfoProvider from '@canvas-builder/components/container/SectionInfoProvider/SectionInfoProvider.vue'
import SectionListItem from '@canvas-builder/components/ui/SectionList/SectionListItem.vue'
import SectionItemModal from '@canvas-builder/components/ui/SectionList/SectionItemModal.vue'
import PageWrapperPaginated from '@canvas-builder/components/container/CanvasContent/pageWrapper.paginated.vue'
import useContentStore from './content.store.ts'

const props = defineProps<{
  id: ComponentNode['id']
  data: ContentListProps
  sectionListName?: string
  previewMode?: boolean
  style?: CSSProperties
  shouldEditWithApp?: boolean
  hasSectionSelectorApp?: boolean
  printModeIdx?: number
}>()

const emit = defineEmits<{
  justAdded: [id: ComponentNode['id']]
  browseWithAppClicked: []
}>()

const componentMap = {
  Grid,
  Carousel,
  ContentGrid,
  DataCharts,
  DynamicDataTable,
  Embeddable,
  Text,
  Html,
  IFrame,
  Link,
  Multimedia,
  Timeline,
  CollectionPlayer,
  Scribble,
} as Record<ComponentTypes, any>
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const canvasHookApps = inject<ComputedRef<AppFile[]>>(
  'canvasHookApps',
  computed(() => []),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const rawItems = computed(() => props.data.sections)
const fetchCanvas = inject<CanvasBuilderProps['fetchCanvas']>('fetchCanvas')

const { watchMissingFiles, files } = useContentStore()

watchMissingFiles(rawItems)

const activeCanvasSections = computed(() => {
  const { getVisibleKey, getVisibleCanvasContent } = useCanvasVisibility()
  const { activeCanvasSections: canvasSections } = useCanvas()
  const visibleKey = getVisibleKey()

  return (rawItems?.value ?? []).reduce(
    (sections, section, nodeIdx) => {
      const foundSection = canvasSections.value?.[section.id]
      const file = section.file_id ? files.loaded?.[section.file_id] : undefined

      if (foundSection) {
        const sectionData = cloneDeep(foundSection)
        const filteredContent = getVisibleCanvasContent(sectionData?.content?.data ?? {}, visibleKey)

        sections.push({
          type: section.type,
          page_index: section.page_index,
          id: section.id,
          file_id: section.file_id,
          component_id: section.component_id,
          sequence: section.sequence,
          file,
          section: {
            ...sectionData,
            content: { ...(sectionData.content ?? {}), data: filteredContent },
          },
          index: nodeIdx,
        })
      }

      return sections
    },
    [] as (ContentListSection & { index: number; file?: FileRetrieve; section: CanvasSection })[],
  )
})

const sectionListRef = ref<HTMLDivElement | null>(null)
const { toast } = useToast()
const { t } = useI18n()
const themeVars = useThemeVars()
const {
  activateNavigationItem,
  activeCanvas,
  canvasContent,
  componentNodesById,
  deactivateNavigationItem,
  isAdmin,
  isDefaultDisplayMode,
  isEditMode,
  isPrintDisplayMode,
  justAddedComponentId,
  listFiles,
  listRecommendedFiles,
  mode,
  onComponentEnter,
  onComponentExit,
  save,
  saveCanvasContent,
  setActiveCanvas,
  updateNodeDataById,
  updateMultimediaNodeWithThemeAsset,
  areThemeAssetsEnabled,
  saveCanvasContentWithContext,
} = useCanvas()
const sectionItemInModal = ref<{ section: CanvasRetrieve['sections'][number]; index: number } | null>(null)
const { isCompletionWizardEnabled } = useCanvasCompletionWizard()

const { updateComponentsWithThemeAssets } = useCanvasNodes()

const { loadSectionThemes, loadSectionThemesAssets, maintainSectionTheme, clearSectionThemes, activeTheme } =
  useCanvasTheme()
const { shouldRenderComponentWrapper } = useCanvasVisibility()
const {
  recommendedSectionIds,
  recommendationStrategy,
  contextSections,
  autofillSectionContentGridsWithMatchingProperties,
} = useRecommendations()
const { currentSectionList, animationDirection, activeSectionId } = useCanvasPages()
const { applyPrintModeToSection } = usePrintMode()
const { hasZeroHeight, updateSectionEmbeddableHeights } = useEmbeddable()

const loadedSections = ref<Record<string, boolean>>({})
const showSelector = ref(false)
const isSavingCanvas = ref(false)
const suggestedSections = ref<(CanvasRetrieve & { selected: boolean })[]>([]) // sections when recommendation strateqy is 'suggest'

// Computed property for the feature flag to avoid repeated checks
const isTocEnhancementsEnabled = computed(
  () => !!launchDarkly.value.enable_toc_enhancements_to_support_same_section_in_section_list,
)

const sectionContentById = computed(() =>
  activeCanvasSections.value.reduce(
    (acc, sectionData) => {
      const { section, index } = sectionData
      acc[section.id] = { content: section.content?.data ?? [], index }
      return acc
    },
    {} as Record<CanvasRetrieve['id'], { content: ComponentNode[]; index: number }>,
  ),
)

const sectionListContainerStyle = computed(() => props.style)

const selectedSectionIds = computed(() => {
  return props.data.sections.map((s) => s.id)
})

const displayedSections = computed(() => {
  return isPrintDisplayMode.value
    ? activeCanvasSections.value
    : activeCanvasSections.value?.[currentSectionList.value.sectionListIndex]
      ? [activeCanvasSections.value[currentSectionList.value.sectionListIndex]]
      : []
})

const { getEntryForSectionHook, setHookRefs, onSectionListUpdateHook, cleanup: cleanupCanvasHook } = useCanvasHooks()

const filteredSectionHookApps = computed(() => {
  return canvasHookApps.value?.filter?.((a: AppFile) => a.app_metadata?.hooks?.canvas?.on_section_list_update) ?? []
})
function getAnimationClass() {
  let animation = props.data?.animation_in
  if (!animation || animation === 'none') return ''
  if (animationDirection.value) {
    animation = animation.replace(/left|right/gi, animationDirection.value)
  }
  return `animate__animated animate__${animation}`
}

function setCanvasHookRefs(iframeEl: HTMLIFrameElement | null, app: AppFile) {
  console.log('setCanvasHookRefs', iframeEl, app)
  if (iframeEl && app) {
    iframeEl.onload = () => {
      if (activeCanvas.value) {
        onSectionListUpdateHook({
          sectionHookIframes: { [app.id]: iframeEl },
          sectionListIds: activeCanvasSections.value.map(({ section: { id } }) => id),
          sectionListComponentId: props.id,
          activeCanvas: deepToRaw(activeCanvas.value),
        })
      }
    }

    setHookRefs(iframeEl, app, CANVAS_HOOKS.ON_SECTION_LIST_UPDATE)
  }
}

function onSectionSelectorToggle() {
  console.log('onSectionSelectorToggle', props.shouldEditWithApp)
  if (props.shouldEditWithApp) emit('browseWithAppClicked')
  else showSelector.value = !showSelector.value
}

function onElementVisibility(isVisible: boolean, c: ComponentNode) {
  const params: ComponentTrackingParams = { trackable: false }

  if (isVisible) onComponentEnter.value?.(c, params)
  else onComponentExit.value?.(c, params)
}

function onSectionVisibility(isVisible: boolean, section: CanvasRetrieve['sections'][number], sectionIdx?: number) {
  reloadSectionThemes(isVisible, section)
  lazyLoadSections(isVisible, section, sectionIdx)
  trackSections(isVisible, section, sectionIdx)
}

function updateSectionMultimediaAssets(
  section: CanvasRetrieve['sections'][number],
  assetInfo: { id: string; name: string; url: string } | null,
  isLogo: boolean = false,
  assetIndex: number = -1,
) {
  if (!section || !section.content?.data) return false

  try {
    // Skip if theme assets feature isn't enabled
    if (!areThemeAssetsEnabled.value) return false

    let needsSectionUpdate = false

    // Process all multimedia components in the section
    const processComponents = (components: ComponentNode[]) => {
      components.forEach((component) => {
        if (component.type === ComponentTypes.Multimedia && component.theme_meta) {
          const { is_theme_logo, theme_asset_index } = component.theme_meta as ComponentNodeMultimediaThemeMeta

          // Check if this is the component we want to update
          if (
            (isLogo && is_theme_logo) ||
            (!isLogo && theme_asset_index !== undefined && theme_asset_index === assetIndex)
          ) {
            if (assetInfo) {
              // Update with the appropriate asset
              const success = updateMultimediaNodeWithThemeAsset(
                component.id,
                {
                  asset_type: 'pitcher_asset',
                  file: {
                    id: assetInfo.id,
                    name: assetInfo.name || (isLogo ? 'Theme Logo' : `Theme Asset ${assetIndex + 1}`),
                    content_url: assetInfo.url,
                  },
                  type: 'image',
                  horizontal_alignment: (component.data as any)?.horizontal_alignment,
                },
                { node: component, section },
              )
              if (success) needsSectionUpdate = true
            } else {
              // Clear the asset if none exists
              const success = updateMultimediaNodeWithThemeAsset(component.id, { type: 'image' } as any, {
                node: component,
                section,
              })
              if (success) needsSectionUpdate = true
            }
          }
        }

        // Process children recursively
        if (component.children?.length) {
          processComponents(component.children)
        }
      })
    }

    // Process all components in the section
    processComponents(section.content.data)

    return needsSectionUpdate
  } catch (e) {
    console.error('Error in updateSectionMultimediaAssets:', e)
    return false
  }
}

function reloadSectionThemes(isVisible: boolean, section: CanvasRetrieve['sections'][number]) {
  if (!isVisible || !launchDarkly.value.enable_themes || !activeCanvas.value?.sections?.length) {
    return
  }

  // Check if section inherits from parent - in that case we don't need to process it separately
  if (section.theme?.inherit_theme_from_parent) {
    clearSectionThemes(undefined, section)
    return
  }

  try {
    if (maintainSectionTheme.value) {
      // Apply section typography
      loadSectionThemes(activeCanvas.value.sections, section)

      // Check if section has its own theme
      if (section.theme) {
        // Only process theme assets if the feature is enabled
        if (areThemeAssetsEnabled.value) {
          const assetsUpdated = { value: false }

          // First handle theme logo if present
          if (section.theme.logo) {
            const logoUpdated = updateSectionMultimediaAssets(
              section,
              {
                id: section.theme.logo.id,
                name: section.theme.logo.name || 'Section Theme Logo',
                url: section.theme.logo.url,
              },
              true,
            )

            if (logoUpdated) assetsUpdated.value = true
          }

          // Then handle theme assets
          if (section.theme.assets?.length) {
            section.theme.assets.forEach((asset: { id: string; name: string; url: string }, index: number) => {
              const assetUpdated = updateSectionMultimediaAssets(
                section,
                {
                  id: asset.id,
                  name: asset.name || `Section Theme Asset ${index + 1}`,
                  url: asset.url,
                },
                false,
                index,
              )

              if (assetUpdated) assetsUpdated.value = true
            })
          }

          // Only use the fallback method if we didn't update all assets
          if (!assetsUpdated.value) {
            // Use a separate function call to avoid duplicating assets
            loadSectionThemesAssets(activeCanvas.value.sections, updateMultimediaNodeWithThemeAsset, section)
          }
        }
      }
    } else {
      // If not maintaining section themes, ensure this section uses the canvas theme
      clearSectionThemes(activeCanvas.value.sections, section)
    }
  } catch (e) {
    console.error('Error in reloadSectionThemes:', e)

    // Fallback to basic functionality
    if (maintainSectionTheme.value) {
      loadSectionThemes(activeCanvas.value.sections, section)
    } else {
      clearSectionThemes(activeCanvas.value.sections, section)
    }
  }
}

function trackSections(isVisible: boolean, section: CanvasRetrieve['sections'][number], sectionIdx?: number) {
  const params: ComponentTrackingParams = { trackable: true }

  if (isVisible) {
    onComponentEnter.value?.(section as CanvasRetrieve, params)
    activateNavigationItem(props.id, section.id, props.previewMode, sectionIdx)
  } else {
    onComponentExit.value?.(section as CanvasRetrieve, params)
    deactivateNavigationItem(props.id, section.id, props.previewMode, sectionIdx)
  }
}

function isSectionLoaded(section: CanvasRetrieve['sections'][number], sectionIdx?: number): boolean {
  if (isPrintDisplayMode.value) return true

  // Only use composite key if feature flag is enabled and sectionIdx is provided
  const sectionKey =
    isTocEnhancementsEnabled.value && sectionIdx !== undefined ? `${section.id}_${sectionIdx}` : section.id

  return loadedSections.value[sectionKey] || false
}

function lazyLoadSections(isVisible: boolean, section: CanvasRetrieve['sections'][number], sectionIdx?: number) {
  // Use composite key for non-print modes when feature flag is enabled and sectionIdx is provided, simple section ID otherwise
  const sectionKey = isPrintDisplayMode.value
    ? section.id
    : isTocEnhancementsEnabled.value && sectionIdx !== undefined
      ? `${section.id}_${sectionIdx}`
      : section.id

  loadedSections.value[sectionKey] = isVisible

  // If the section becomes visible, ensure section themes are reloaded
  if (isVisible && launchDarkly.value.enable_themes && maintainSectionTheme.value) {
    reloadSectionThemes(true, section)
  }
}

function maybeCloseSectionSelector(isForcedRecommendations: boolean) {
  if (!isForcedRecommendations) showSelector.value = false
}

async function onSectionsSelect(items: { id: CanvasRetrieve['id'] }[], isForcedRecommendations = false) {
  try {
    const sections = items.map(({ id }) => ({ id }))
    if (!activeCanvas.value?.id || isEqual(sections, props.data.sections)) {
      maybeCloseSectionSelector(isForcedRecommendations)
      return
    }

    const previousSections = cloneDeep(props.data.sections)
    isSavingCanvas.value = true
    updateNodeDataById(props.id, { ...props.data, sections }) // optimistically update the sections
    const c = await save(
      activeCanvas.value.id,
      {
        content: { ...(activeCanvas.value?.content ?? {}), data: canvasContent.value },
      },
      false,
    ).catch((error) => {
      updateNodeDataById(props.id, { ...props.data, sections: previousSections }) // update failed, revert back to previous sections
      toast.error((error as Error)?.message)
      throw error
    })

    console.log('onSectionListUpdateHook', items)
    onSectionListUpdateHook({
      sectionListIds: sections.map(({ id }) => id),
      sectionListComponentId: props.id,
      activeCanvas: deepToRaw(activeCanvas.value),
    })

    if (c && c.id === activeCanvas.value?.id) {
      setActiveCanvas(c)
    } else {
      console.warn('[SectionList.raw.vue]: Patch does not concern active canvas:', {
        activeCanvasId: activeCanvas.value?.id,
        patchedCanvas: c,
      })
    }

    if (launchDarkly.value.enable_themes && activeCanvas.value?.sections?.length) {
      if (maintainSectionTheme.value) {
        // If maintaining section themes, apply section typography and assets
        loadSectionThemes(activeCanvas.value.sections)

        try {
          // Only load theme assets if the feature is enabled
          if (areThemeAssetsEnabled.value) {
            loadSectionThemesAssets(activeCanvas.value.sections, updateMultimediaNodeWithThemeAsset)
          }
        } catch (e) {
          console.error('Error loading section theme assets:', e)
        }
      } else {
        // If not maintaining section themes, clear section themes
        clearSectionThemes(activeCanvas.value.sections)

        try {
          // Re-apply canvas theme to all multimedia components to ensure they use canvas theme assets
          if (activeTheme.value) {
            if (areThemeAssetsEnabled.value) {
              updateComponentsWithThemeAssets(
                activeTheme.value,
                componentNodesById.value as Record<string, ComponentNode>,
                updateNodeDataById,
                activeCanvas.value,
              )
            }
          }
        } catch (e) {
          console.error('Error applying canvas theme assets:', e)
        }
      }
    }

    isSavingCanvas.value = false
    maybeCloseSectionSelector(isForcedRecommendations)
  } catch (error) {
    isSavingCanvas.value = false
    toast.error((error as Error)?.message)
  }
}

function keepRecommendedSections() {
  onSectionsSelect(suggestedSections.value.filter((s) => s.selected).map(({ selected, ...s }) => s))
}

function checkAndUnloadSections() {
  if (!isEditMode.value) return
  // to ensure sections are unloaded when they are not in viewport. without smooth scrolling, this shouldn't be triggered
  Object.keys(loadedSections.value).forEach((sectionKey) => {
    if (!loadedSections.value[sectionKey]) return

    if (!isPrintDisplayMode.value && isTocEnhancementsEnabled.value && sectionKey.includes('_')) {
      // Composite key approach for non-print modes when feature flag is enabled
      const [sectionId, sectionIdxStr] = sectionKey.split('_')
      const sectionIdx = parseInt(sectionIdxStr, 10)

      // Find the section element with both the correct ID and index
      const sectionElements = document.querySelectorAll(`[data-section-id="${sectionId}"]`)
      if (!sectionElements?.length) return

      // If we have multiple elements with the same ID, get the one at the specified index
      const element = sectionElements.length > sectionIdx ? sectionElements[sectionIdx] : sectionElements[0]

      if (element) {
        const rect = element.getBoundingClientRect()
        const isNotVisible = rect.bottom < 0 || rect.top > window.innerHeight

        if (isNotVisible) {
          loadedSections.value[sectionKey] = false
        }
      }
    } else {
      // Simple approach for print mode or when feature flag is disabled - just use section ID
      const sectionElements = document.querySelectorAll(`[data-section-id="${sectionKey}"]`)
      if (!sectionElements?.length) return

      const isNotVisible = Array.from(sectionElements).every((element) => {
        const rect = element.getBoundingClientRect()
        return rect.bottom < 0 || rect.top > window.innerHeight
      })

      if (isNotVisible) {
        loadedSections.value[sectionKey] = false
      }
    }
  })
}

function getComponentBindings(c: ComponentNode) {
  return c.type === ComponentTypes.Grid || c.type === ComponentTypes.Scribble
    ? c
    : { ...omit(c, 'children'), style: omit(c.style, 'marginBottom') }
}

function getComponentStyle(
  c: ComponentNode,
  section: CanvasRetrieve['sections'][number],
  componentIndex?: number,
  sectionContent?: ComponentNode[],
) {
  const hasIntuitiveSpacing = launchDarkly.value.enable_more_intuitive_component_spacing

  if (hasIntuitiveSpacing && componentIndex !== undefined && sectionContent) {
    const isLastComponent = componentIndex === sectionContent.length - 1
    const isOnlyComponent = sectionContent.length === 1
    const nextComponent = componentIndex < sectionContent.length - 1 ? sectionContent[componentIndex + 1] : undefined

    const nextEmbeddableHasZeroHeight =
      nextComponent?.type === ComponentTypes.Embeddable && nextComponent.id && hasZeroHeight(nextComponent.id)

    if (isLastComponent || isOnlyComponent || nextEmbeddableHasZeroHeight) {
      return {}
    }
  } else {
    // Don't apply marginBottom if component is Scribble and responsive flag is enabled
    if (c.type === ComponentTypes.Scribble && launchDarkly.value.enable_responsive_scribble_component) {
      return {}
    }
  }

  return {
    marginBottom: c?.style?.marginBottom || getSectionGlobalComponentSpacing(section),
  }
}

function getSectionThemeStyle(
  section: CanvasRetrieve['sections'][number] & { theme?: CanvasRetrieve['theme'] },
  style?: CSSProperties,
) {
  if (launchDarkly.value.enable_themes && (!maintainSectionTheme.value || section.theme?.inherit_theme_from_parent)) {
    return { backgroundColor: 'transparent' }
  }

  return {
    ...(section?.theme?.style || {}),
    backgroundColor:
      section?.theme?.style?.backgroundColor || section?.theme?.canvas_bg_color || style?.backgroundColor,
  }
}

function maybeAutofillOnSectionView() {
  if (
    !launchDarkly.value.allow_content_grid_autofill ||
    activeCanvas.value?.is_template ||
    mode.value === 'sharing' ||
    !activeSectionId.value ||
    isCompletionWizardEnabled.value
  ) {
    return
  }

  autofillSectionContentGridsWithMatchingProperties(activeSectionId.value, {
    filesFetcher: listFiles.value as NonNullable<CanvasBuilderProps['listFiles']>,
    recommendedFilesFetcher: listRecommendedFiles.value as NonNullable<CanvasBuilderProps['listRecommendedFiles']>,
    instanceSettings: pitcherSettings.value,
  }).then(() => saveCanvasContent({ showToast: false }))
}

let pollingInterval: ReturnType<typeof setInterval>
onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    emit('justAdded', props.id)
    justAddedComponentId.value = null
  }
  pollingInterval = setInterval(checkAndUnloadSections, 10000) // cleanup leaked v-element-visibility sections every 10 seconds

  if (launchDarkly.value.enable_more_intuitive_component_spacing) {
    updateSectionEmbeddableHeights()
  }

  // Apply print mode rasterization to all sections at once when in print mode
  if (isPrintDisplayMode.value && maintainSectionTheme.value) {
    // Give the DOM time to render completely, load themes, and process sections in a single timeout
    setTimeout(() => {
      // First, ensure section themes are loaded for only the sections this SectionList handles
      if (launchDarkly.value.enable_themes && activeCanvas.value?.sections?.length) {
        // Only load themes for the sections this SectionList instance is responsible for
        activeCanvasSections.value.forEach((sectionData) => {
          const { section } = sectionData
          // Only process sections that don't inherit from parent
          if (!section.theme?.inherit_theme_from_parent && activeCanvas.value?.sections) {
            loadSectionThemes(activeCanvas.value.sections, section)
          }
        })
      }

      // Clear any existing section styling that might be interfering for this instance's sections only
      activeCanvasSections.value.forEach((sectionData) => {
        const { section, index } = sectionData
        // Use both section-id and section-index to target the exact section occurrence
        const sectionElement = document.querySelector(
          `[data-section-id="${section.id}"][data-section-index="${index}"]`,
        ) as HTMLElement

        if (sectionElement) {
          if (sectionElement.hasAttribute('data-theme-processed')) {
            sectionElement.removeAttribute('data-theme-processed')
          }
          if (sectionElement.hasAttribute('data-theme-inherited')) {
            sectionElement.removeAttribute('data-theme-inherited')
          }
        }
      })

      // Then process each section with a unique index to track order
      activeCanvasSections.value.forEach((sectionData, localIndex) => {
        const { section, index } = sectionData
        const sectionId = section.id
        // Use both section-id and section-index to target the exact section occurrence
        const sectionElement = document.querySelector(
          `[data-section-id="${sectionId}"][data-section-index="${index}"]`,
        ) as HTMLElement

        if (sectionElement) {
          // Add processing order attribute
          sectionElement.setAttribute('data-processing-order', `${localIndex + 1}`)

          // Apply the section's theme
          applyPrintModeToSection(section, sectionElement, maintainSectionTheme.value)
        }
      })
    }, 500) // Single delay to ensure DOM is fully rendered and themes are loaded
  }
})

const aspectRatio = computed(() => {
  const rawRatio: string | undefined = activeCanvas.value?.theme?.aspect_ratio

  if (!rawRatio || rawRatio === 'contain' || rawRatio === 'unlocked') return undefined

  const [x, y] = rawRatio.split(':').map((n) => parseInt(n))

  if (isNaN(x) || isNaN(y)) return undefined

  return {
    x,
    y,
  }
})

const aspectRatioStyle = computed(() => {
  if (!aspectRatio.value || isPrintDisplayMode.value) return undefined

  const { x, y } = aspectRatio.value

  return {
    aspectRatio: `${x} / ${y}`,
  }
})

const isScaleEnabled = inject<ComputedRef<boolean>>(
  'isScaleEnabled',
  computed(() => false),
)

onUnmounted(() => {
  clearInterval(pollingInterval)
  for (const app of filteredSectionHookApps.value) {
    cleanupCanvasHook(app, CANVAS_HOOKS.ON_SECTION_LIST_UPDATE)
  }
})

defineExpose({
  toggleSectionSelector: onSectionSelectorToggle,
  onSectionsSelect: onSectionsSelect,
})

if (mode.value === CanvasBuilderMode.IMPACT) {
  watch(
    recommendedSectionIds,
    async (oldValue, newValue) => {
      if (isEqual(oldValue, newValue)) return

      if (recommendationStrategy.value === 'suggest') {
        console.info('With suggest strategy, recommended section ids changed: ', recommendedSectionIds.value)
        const sectionIdsToFetch = recommendedSectionIds.value.filter(
          (id) => !suggestedSections.value.some((s) => s.id === id),
        )

        if (activeCanvasSections.value.length || !fetchCanvas || !sectionIdsToFetch.length) return

        const results = await Promise.allSettled(sectionIdsToFetch.map((id) => fetchCanvas(id)))
        suggestedSections.value = [
          ...suggestedSections.value,
          ...results
            .filter((result): result is PromiseFulfilledResult<CanvasRetrieve> => result.status === 'fulfilled')
            .map((result) => ({ ...result.value, selected: false })),
        ]
      } else if (recommendationStrategy.value === 'force') {
        console.info('With force strategy, recommended section ids changed: ', recommendedSectionIds.value)
        const forcedSections = contextSections.value.filter((s) => !s.removed_by_user).map((s) => ({ id: s.id }))
        console.info('Forcing recommendations: ', forcedSections)
        await onSectionsSelect(forcedSections, true)
      }
    },
    { immediate: true },
  )

  if ((pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') === 'pagination') {
    watch(
      activeSectionId,
      (newId, oldId) => {
        if (newId !== oldId) maybeAutofillOnSectionView()
      },
      { immediate: true },
    )
  }
}
</script>

<style scoped lang="scss">
@media print {
  .canvas-section {
    &:not(:last-child) {
      break-after: page;
    }
  }
}

.canvas-section-list__idle-state__title {
  color: v-bind('themeVars.primary3');
  line-height: 32px;
  letter-spacing: 0.25px;
  font-size: 20px;
}

// prevent scrolling outside of the Modal (SectionItemModal) in Safari
.canvas-section-list-container :deep(.n-modal-scroll-content) {
  overflow: hidden;
}
</style>
